import React, { useMemo, useState } from 'react';
import { MdLite } from './mdlite';
import { delta, deltaLabel, fmt, pct } from './palette';

type Row = Record<string, unknown>;
const num = (v: unknown): number => (typeof v === 'number' && isFinite(v) ? v : 0);

// ── KPI row ────────────────────────────────────────────────────────────────
export interface KpiDef {
    label: string;
    value: number;
    prev?: number;
    accent?: 'accent' | 'viewer';
}
export const KpiRow: React.FC<{ kpis: KpiDef[]; days: number; compare: boolean }> = ({ kpis, days, compare }) => (
    <div className="pr-kpi-grid">
        {kpis.map((k) => {
            const d = k.prev !== undefined ? delta(k.value, k.prev) : null;
            return (
                <div key={k.label} className={`pr-kpi ${k.accent ?? ''}`}>
                    <div className="label">{k.label}</div>
                    <div className="value">{fmt(k.value)}</div>
                    {compare && d && (
                        <div className={`delta ${d.cls}`}>{d.text} {d.text !== '—' && deltaLabel(days)}</div>
                    )}
                </div>
            );
        })}
    </div>
);

// ── New-user funnel ────────────────────────────────────────────────────────
interface FunnelBranch {
    label: string; kind: string; entered: number; published: number; dropped: number;
    prev_entered: number; prev_published: number;
}
export interface FunnelData {
    new_users: number; prev_new_users: number; signed_up: number; prev_signed_up: number;
    no_action: number; prev_no_action: number; published_total: number;
    prev_published_total: number; suspect: boolean; branches: FunnelBranch[];
}
export const NewUserFunnel: React.FC<{ funnel?: FunnelData; days: number }> = ({ funnel, days }) => {
    if (!funnel || !funnel.new_users) {
        return <div className="pr-card"><h3>New-user funnel — where they drop</h3>
            <div className="pr-empty">No new users in this window.</div></div>;
    }
    const fn = funnel;
    return (
        <div className="pr-card">
            <h3>New-user funnel — where they drop (last {days} days vs previous {days})</h3>
            <div className="nf">
                <div className="nf-signup">
                    <div className="nf-box">
                        <div className="nf-name">New editor users</div>
                        <div className="nf-count">{fmt(fn.new_users)}</div>
                        <div className="nf-prev">last period: {fmt(fn.prev_new_users)}</div>
                        <div className="nf-prev">signup events: {fmt(fn.signed_up)}</div>
                        <div className="nf-drop">took no action: {fmt(fn.no_action)} ({pct(fn.no_action, fn.new_users)}%)</div>
                    </div>
                </div>
                <div className="nf-branches">
                    {fn.branches.map((b) => (
                        <div className="nf-row" key={b.label}>
                            <div className="nf-arrow" />
                            <div className="nf-box">
                                <div className="nf-name">{b.label}</div>
                                <div className="nf-count">{fmt(b.entered)}</div>
                                <div className="nf-prev">last period: {fmt(b.prev_entered)}</div>
                            </div>
                            <div className="nf-arrow" />
                            <div className="nf-box">
                                <div className="nf-name">Published</div>
                                <div className="nf-count">{fmt(b.published)}</div>
                                <div className="nf-prev">last period: {fmt(b.prev_published)}</div>
                                {b.entered > 0 && (
                                    <div className="nf-drop">dropped: {fmt(b.dropped)} ({pct(b.dropped, b.entered)}%)</div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="nf-note">
                Published via any path: <strong>{fmt(fn.published_total)}</strong> (last
                period {fmt(fn.prev_published_total)}). Cohort = persons first seen inside the window
                who fired at least one tracked editor event (viewer readers are excluded). A branch's
                publish requires the first publish at/after the first branch action; one user can
                appear in several branches. The signup event rides a separate identity (Firebase
                UID), so branches measure against all new editor users, not signups.
            </div>
            {fn.suspect && (
                <div className="nf-warn">
                    ⚠️ More new users published than registered any creation signal — the creation
                    events/intents under-fire (known Editor-Frontend gaps: tree “New” menu,
                    child-topic menu, and auto-created first topics are uninstrumented); treat the
                    create branches as a lower bound.
                </div>
            )}
        </div>
    );
};

// ── Sortable feature table ─────────────────────────────────────────────────
interface Col { key: string; label: string; derive?: (r: Row) => number }
export const FeatureTable: React.FC<{
    rows: Row[]; prevRows?: Row[]; days: number; compare: boolean; title: string; caption?: string;
}> = ({ rows, prevRows, days, compare, title, caption }) => {
    const [sort, setSort] = useState<{ key: string; dir: 1 | -1 }>({ key: 'events', dir: -1 });
    const prevMap = useMemo(
        () => new Map((prevRows ?? []).map((r) => [String(r.event), r])),
        [prevRows],
    );
    const cols: Col[] = useMemo(() => {
        const base: Col[] = [
            { key: 'events', label: 'Events' },
            { key: 'users', label: 'Users' },
            { key: 'new_events', label: 'New-user events' },
            {
                key: 'ret_events', label: 'Returning-user events',
                derive: (r) => num(r.events) - Math.min(num(r.new_events), num(r.events)),
            },
        ];
        return base;
    }, []);
    const sorted = useMemo(() => {
        const col = cols.find((c) => c.key === sort.key);
        const val = (r: Row) =>
            sort.key === 'event' ? String(r.event) : col?.derive ? col.derive(r) : num(r[sort.key]);
        return [...(rows ?? [])].sort((a, b) => {
            const av = val(a); const bv = val(b);
            return (av < bv ? -1 : av > bv ? 1 : 0) * sort.dir;
        });
    }, [rows, sort, cols]);
    const toggle = (key: string) =>
        setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: -1 }));
    if (!rows?.length) return null;
    return (
        <div className="pr-card">
            <h3>{title}</h3>
            <table className="pr-table">
                <thead><tr>
                    <th onClick={() => toggle('event')}>Feature</th>
                    <th onClick={() => toggle('events')}>Events</th>
                    {compare && <th>Δ {deltaLabel(days)}</th>}
                    <th onClick={() => toggle('users')}>Users</th>
                    <th onClick={() => toggle('new_events')}>New-user events</th>
                    <th onClick={() => toggle('ret_events')}>Returning-user events</th>
                </tr></thead>
                <tbody>
                    {sorted.map((r) => {
                        const events = num(r.events);
                        const newEv = Math.min(num(r.new_events), events);
                        const p = prevMap.get(String(r.event));
                        const d = delta(events, num(p?.events));
                        return (
                            <tr key={String(r.event)}>
                                <td>{String(r.event)}</td>
                                <td>{fmt(events)}</td>
                                {compare && <td className={d.cls}>{d.text}</td>}
                                <td>{fmt(num(r.users))}</td>
                                <td>{fmt(newEv)}</td>
                                <td>{fmt(events - newEv)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {caption && <p className="pr-caption" style={{ marginTop: 8 }}>{caption}</p>}
        </div>
    );
};

// ── Small tables ───────────────────────────────────────────────────────────
export const NavTable: React.FC<{ rows: Row[] }> = ({ rows }) => {
    if (!rows?.length) return null;
    return (
        <div className="pr-card">
            <h3>Where users navigate (appbar sections)</h3>
            <table className="pr-table">
                <thead><tr><th>Destination</th><th>Clicks</th><th>Users</th></tr></thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i}>
                            <td>{r.destination == null ? '(none)' : String(r.destination)}</td>
                            <td>{fmt(num(r.events))}</td>
                            <td>{fmt(num(r.users))}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export const TopicViews: React.FC<{ data?: { rows?: Row[]; error?: string } }> = ({ data }) => {
    if (!data) return null;
    if (data.error) {
        return <div className="pr-card"><h3>Topic views by language (BigQuery)</h3>
            <p className="pr-caption">Topic-view data unavailable this run: {data.error}</p></div>;
    }
    if (!data.rows?.length) return null;
    return (
        <div className="pr-card">
            <h3>Topic views by language (BigQuery)</h3>
            <table className="pr-table">
                <thead><tr><th>Language</th><th>Views</th></tr></thead>
                <tbody>
                    {data.rows.slice(0, 15).map((r, i) => (
                        <tr key={i}>
                            <td>{String(r.language_code)}</td>
                            <td>{fmt(num(r.views))}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// ── Findings + data quality ────────────────────────────────────────────────
export interface Finding { scope: string; severity: string; text: string }
export const Findings: React.FC<{ findings: Finding[] }> = ({ findings }) => (
    <div className="pr-card">
        <h3>Weaknesses &amp; recommendations</h3>
        {findings.map((f, i) => (
            <div key={i} className={`pr-finding ${f.severity}`}>
                <span className={`pr-badge ${f.scope}`}>{f.scope}</span>
                <span><MdLite text={f.text} /></span>
            </div>
        ))}
    </div>
);

export const DataQuality: React.FC<{ errors: Array<{ section: string; error: string }> }> = ({ errors }) => {
    if (!errors?.length) return null;
    return (
        <div className="pr-card">
            <h3>Data quality</h3>
            {errors.map((e, i) => (
                <div key={i} className="pr-finding">
                    <span className="pr-badge meta">⚠️</span>
                    <span><code>{e.section}</code>: {e.error}</span>
                </div>
            ))}
        </div>
    );
};

// ── Totals — every tracked event, window + since-instrumentation ───────────
interface TotalsEditorRow {
    event: string; plan: string; events_window: number; events_all: number; users_window: number;
}
interface TotalsViewerRow {
    event: string; events_window: number; events_all: number; users_window: number;
}
export interface TotalsData {
    editor_events?: TotalsEditorRow[];
    viewer_events?: TotalsViewerRow[];
    topics_by_type?: Array<{ content_type: string; topics_window: number; topics_all: number }>;
    all_editor_events?: string[];
    all_viewer_events?: string[];
}
export const PLAN_ORDER = ['All', 'Free', 'Business', 'Business Annual', 'Enterprise', 'Enterprise Old', 'Unknown'];
